import React, { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─────────────────────────────────────────────────────────────
 * ShareLinkBar: Copy + Web Share + WhatsApp + Email buttons
 * ────────────────────────────────────────────────────────────*/
export function ShareLinkBar({ url, label = "Share this portal" }) {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* noop */ }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Your Folk Studio portal", url }); } catch { /* user dismissed */ }
    } else {
      copyLink();
    }
  };

  const waHref = `https://wa.me/?text=${encodeURIComponent(`Your Folk portal is ready → ${url}`)}`;
  const emailHref = `mailto:?subject=${encodeURIComponent("Your Folk Studio portal")}&body=${encodeURIComponent(`Everything about our project lives here: ${url}`)}`;

  return (
    <div data-testid="share-link-bar" style={{ background: "#BADDF0", border: "1.5px solid rgba(27, 74, 102, 0.2)", borderRadius: 14, padding: "14px 16px", maxWidth: 820 }}>
      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#0F3A54", fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ fontSize: 13, color: "#0F3A54", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "1 1 240px", minWidth: 0, opacity: 0.85 }}>{url}</div>
        <button data-testid="share-copy-btn" type="button" onClick={copyLink} style={smallPill}>{copied ? "Copied!" : "Copy"}</button>
        <a data-testid="share-whatsapp-btn" href={waHref} target="_blank" rel="noreferrer" style={smallPill}>WhatsApp</a>
        <a data-testid="share-email-btn" href={emailHref} style={smallPill}>Email</a>
        <button data-testid="share-native-btn" type="button" onClick={nativeShare} style={{ ...smallPill, background: "#68280E", color: "#FFF", border: "1.5px solid #68280E" }}>Share…</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * LeaveMessageWidget: async "leave us a message" ticket system
 * ────────────────────────────────────────────────────────────*/
export function LeaveMessageWidget({ submissionId, defaultEmail }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list"); // "list" | "compose"
  const [queries, setQueries] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const load = React.useCallback(async () => {
    if (!submissionId) return;
    try {
      const { data } = await axios.get(`${API}/portal/${submissionId}/queries`);
      setQueries(data || []);
    } catch { /* silent */ }
  }, [submissionId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    const openHandler = () => setOpen(true);
    window.addEventListener("folk:open-leave-message", openHandler);
    return () => { clearInterval(iv); window.removeEventListener("folk:open-leave-message", openHandler); };
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim() || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/portal/${submissionId}/queries`, {
        subject: subject.trim(),
        message: message.trim(),
        email: defaultEmail || null,
      });
      setSubject("");
      setMessage("");
      setSent(true);
      await load();
      setTimeout(() => { setSent(false); setView("list"); }, 1600);
    } catch { /* keep the draft */ }
    finally { setBusy(false); }
  };

  const awaitingReply = queries.filter((q) => q.status === "open").length;
  const badge = awaitingReply > 0 && !open;

  return (
    <>
      <button
        type="button"
        data-testid="leave-message-fab"
        onClick={() => setOpen((v) => !v)}
        style={{
          position: "fixed", bottom: 22, right: 22, zIndex: 30,
          background: "#68280E", color: "#FFF", border: "none",
          borderRadius: 999, padding: "14px 22px", fontSize: 15, fontWeight: 500,
          fontFamily: "Host Grotesk, sans-serif", cursor: "pointer",
          boxShadow: "0 8px 24px rgba(104, 40, 14, 0.25)",
          display: "inline-flex", alignItems: "center", gap: 8,
        }}
      >
        {open ? "Close" : "Leave us a message"}
        {badge && <span data-testid="leave-message-badge" style={{ background: "#FFFBC9", color: "#68280E", borderRadius: 999, padding: "2px 8px", fontSize: 12, fontWeight: 700 }}>{awaitingReply}</span>}
      </button>
      {open && (
        <div
          data-testid="leave-message-panel"
          style={{
            position: "fixed", bottom: 82, right: 22, zIndex: 30,
            width: 380, maxHeight: 560, background: "#FFF",
            border: "1.5px solid var(--folk-border)", borderRadius: 18,
            boxShadow: "0 16px 40px rgba(0, 0, 0, 0.12)",
            display: "flex", flexDirection: "column", overflow: "hidden",
          }}
        >
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--folk-border)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div>
              <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 600 }}>Leave us a message</div>
              <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.028em', fontSize: 18, textTransform: "lowercase" }}>we reply within 1–2 business days.</div>
            </div>
            {view === "list" && (
              <button type="button" data-testid="leave-message-new-btn" onClick={() => setView("compose")} className="folk-pill" style={{ padding: "6px 14px", fontSize: 12, whiteSpace: "nowrap" }}>New</button>
            )}
            {view === "compose" && (
              <button type="button" data-testid="leave-message-back-btn" onClick={() => setView("list")} style={{ background: "none", border: "none", color: "var(--folk-muted)", cursor: "pointer", fontFamily: "Host Grotesk, sans-serif", fontSize: 13, textDecoration: "underline" }}>Back</button>
            )}
          </div>

          {view === "list" && (
            <div data-testid="leave-message-list" style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10, background: "#FAF5F3", minHeight: 240 }}>
              {queries.length === 0 && (
                <div style={{ color: "var(--folk-muted)", fontSize: 14, lineHeight: 1.55, padding: "8px 4px" }}>
                  No messages yet. Tap <strong>New</strong> to send us a question — our team will email you back within a business day or two.
                </div>
              )}
              {queries.map((q) => (
                <div key={q.id} data-testid={`query-${q.id}`} style={{ background: "#FFF", border: "1px solid var(--folk-border)", borderRadius: 12, padding: "12px 14px", fontFamily: "Host Grotesk, sans-serif" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#1B1A1A", flex: 1 }}>{q.subject}</div>
                    <span data-testid={`query-status-${q.id}`} style={{
                      fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700,
                      background: q.status === "replied" ? "#BADDF0" : "#FFFBC9",
                      color: q.status === "replied" ? "#0F3A54" : "#68280E",
                      borderRadius: 999, padding: "2px 10px", whiteSpace: "nowrap",
                    }}>{q.status === "replied" ? "Replied" : "Open"}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "#5A5757", lineHeight: 1.5, whiteSpace: "pre-wrap", marginBottom: 8 }}>{q.message}</div>
                  <div style={{ fontSize: 11, color: "var(--folk-hint)", marginBottom: q.status === "replied" ? 10 : 0 }}>
                    Sent {new Date(q.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                  </div>
                  {q.status === "replied" && q.admin_reply && (
                    <div style={{ borderTop: "1px dashed rgba(27,26,26,0.14)", paddingTop: 10, marginTop: 4 }}>
                      <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700, marginBottom: 6 }}>Folk team reply</div>
                      <div style={{ background: "#FFFBC9", borderRadius: 10, padding: "10px 12px", fontSize: 13.5, lineHeight: 1.55, whiteSpace: "pre-wrap", color: "#1B1A1A" }}>{q.admin_reply}</div>
                      <div style={{ fontSize: 11, color: "var(--folk-hint)", marginTop: 4 }}>
                        {new Date(q.replied_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {view === "compose" && (
            <form onSubmit={submit} data-testid="leave-message-form" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10, background: "#FAF5F3" }}>
              <label style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-muted)", fontWeight: 600 }}>Subject</label>
              <input
                data-testid="leave-message-subject"
                type="text"
                placeholder="A quick question about…"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={140}
                className="folk-input"
                style={{ padding: "10px 14px", fontSize: 14 }}
              />
              <label style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-muted)", fontWeight: 600 }}>Your message</label>
              <textarea
                data-testid="leave-message-body"
                placeholder="Share as much detail as you'd like — we'll reply within 1–2 business days."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={4000}
                rows={6}
                className="folk-input"
                style={{ padding: "10px 14px", fontSize: 14, resize: "vertical", fontFamily: "Host Grotesk, sans-serif" }}
              />
              <button
                type="submit"
                data-testid="leave-message-send-btn"
                className="folk-pill"
                disabled={busy || !subject.trim() || !message.trim()}
                style={{ padding: "12px 18px", fontSize: 14 }}
              >
                {sent ? "Sent!" : busy ? "Sending…" : "Send message"}
              </button>
              <div style={{ fontSize: 12, color: "var(--folk-hint)", lineHeight: 1.5 }}>
                We'll email you the reply and it'll show up here too.
              </div>
            </form>
          )}
        </div>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
 * ReactionRow: 4 emoji reactions with live counts
 * ────────────────────────────────────────────────────────────*/
const EMOJIS = ["💛", "🔥", "👀", "❓"];

export function ReactionRow({ submissionId, updateId, initial = {} }) {
  const [reactions, setReactions] = useState(initial);
  const [busy, setBusy] = useState(false);

  const react = async (emoji) => {
    if (busy) return;
    setBusy(true);
    setReactions((r) => ({ ...r, [emoji]: (r[emoji] || 0) + 1 }));
    try {
      const { data } = await axios.post(`${API}/portal/${submissionId}/updates/${updateId}/react`, { emoji });
      setReactions(data.reactions || {});
    } catch { /* keep optimistic */ }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`reaction-row-${updateId}`} style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
      {EMOJIS.map((e) => (
        <button
          key={e}
          type="button"
          data-testid={`reaction-${updateId}-${e}`}
          onClick={() => react(e)}
          style={{
            background: reactions[e] ? "#FFFBC9" : "var(--folk-card)",
            border: "1px solid var(--folk-border)",
            borderRadius: 999, padding: "4px 10px", fontSize: 14,
            cursor: "pointer", fontFamily: "Host Grotesk, sans-serif",
            display: "inline-flex", alignItems: "center", gap: 4,
          }}
        >
          <span>{e}</span>
          <span style={{ fontSize: 12, color: "var(--folk-muted)" }}>{reactions[e] || 0}</span>
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * FirstVisitTour: 4-step coach marks, dismissable
 * ────────────────────────────────────────────────────────────*/
export function FirstVisitTour({ submissionId }) {
  const key = `folk-tour-seen-${submissionId}`;
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(key)) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, [key]);

  const steps = [
    { title: "This is your Folk Studio.", body: "Every letter, plan, render, note and update your team posts will land right here, always up to date." },
    { title: "Follow live progress.", body: "The Project status tab shows what phase your home is in and every update we've posted, refreshed every 30 seconds." },
    { title: "Your visual language lives here.", body: "The Moodboard tab captures everything you chose in the survey. Renders and Layouts fill up as we design." },
    { title: "Something on your mind?", body: "Tap Leave us a message at the bottom-right any time. We'll email you the reply and it'll show up in your portal too." },
  ];

  if (!visible) return null;
  const s = steps[step];
  const last = step === steps.length - 1;

  const close = () => { localStorage.setItem(key, "1"); setVisible(false); };

  return (
    <div data-testid="tour-overlay" style={{ position: "fixed", inset: 0, background: "rgba(27, 26, 26, 0.55)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#FFF", borderRadius: 20, padding: 32, maxWidth: 460, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
          {steps.map((_, i) => (
            <div key={i} style={{ flex: 1, height: 4, borderRadius: 4, background: i <= step ? "#68280E" : "#EFE5E1" }} />
          ))}
        </div>
        <div style={{ fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: "#68280E", fontWeight: 600, marginBottom: 8 }}>Welcome tour · {step + 1}/{steps.length}</div>
        <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 26, lineHeight: 1.2, marginBottom: 10 }}>{s.title}</div>
        <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 15.5, lineHeight: 1.55, color: "#2A2828", marginBottom: 22 }}>{s.body}</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <button type="button" data-testid="tour-skip-btn" onClick={close} style={{ background: "none", border: "none", color: "var(--folk-muted)", cursor: "pointer", fontFamily: "Host Grotesk, sans-serif", fontSize: 14, textDecoration: "underline" }}>Skip</button>
          <button type="button" data-testid="tour-next-btn" onClick={() => (last ? close() : setStep(step + 1))} className="folk-pill">
            {last ? "Let's go" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * StyleAtGlanceCard: auto-derived tags from persona summary
 * ────────────────────────────────────────────────────────────*/
export function StyleAtGlanceCard({ answers }) {
  const styles = (answers?.["Preferred styles"] || "").split(",").map((s) => s.trim()).filter(Boolean);
  const color = answers?.["Color relationship"];
  const fiveWords = (answers?.["Five words"] || "").split(",").map((s) => s.trim()).filter(Boolean);

  const tags = [];
  styles.slice(0, 2).forEach((s) => tags.push({ label: s.charAt(0).toUpperCase() + s.slice(1), hue: "yellow" }));
  if (color) tags.push({ label: color, hue: "blue" });
  fiveWords.slice(0, 2).forEach((w) => tags.push({ label: w, hue: "maroon" }));

  if (tags.length === 0) return null;

  const hueMap = {
    yellow: { bg: "#FFFBC9", color: "#68280E", border: "rgba(104, 40, 14, 0.18)" },
    maroon: { bg: "#68280E", color: "#FFF", border: "rgba(104, 40, 14, 0.35)" },
    blue: { bg: "#BADDF0", color: "#0F3A54", border: "rgba(27, 74, 102, 0.25)" },
  };

  return (
    <div data-testid="style-at-glance" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 16, padding: 22, maxWidth: 780, marginBottom: 24 }}>
      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 600, marginBottom: 10 }}>Your Folk style, at a glance</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {tags.map((t, i) => (
          <span key={`${t.label}-${i}`} style={{ ...hueMap[t.hue], padding: "6px 14px", borderRadius: 999, fontSize: 13, fontWeight: 500, border: `1px solid ${hueMap[t.hue].border}` }}>
            {t.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * ReferralCard — personal invite link + 10% off messaging
 * ────────────────────────────────────────────────────────────*/
export function ReferralCard({ submissionId }) {
  const [state, setState] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!submissionId) return;
    let mounted = true;
    axios.get(`${API}/portal/${submissionId}/referrals`)
      .then((r) => { if (mounted) setState(r.data); })
      .catch(() => { /* silent */ });
    return () => { mounted = false; };
  }, [submissionId]);

  const shareUrl = state?.share_url || (submissionId ? "" : `${window.location.origin}/survey`);
  const waHref = state?.whatsapp_url || `https://wa.me/?text=${encodeURIComponent(`I've been using Folk Studio to design our home. You'd love it. Start with their homeowner survey: ${shareUrl}`)}`;
  const discount = state?.discount_percentage || 0;

  const copy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* */ }
  };

  return (
    <div
      data-testid="referral-card"
      style={{
        background: discount > 0 ? "#0F3A54" : "#BADDF0",
        color: discount > 0 ? "#FFFBC9" : "#0F3A54",
        border: `1.5px solid ${discount > 0 ? "#0F3A54" : "rgba(15, 58, 84, 0.24)"}`,
        borderRadius: 22,
        padding: "26px 28px",
        maxWidth: 900,
        marginBottom: 32,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 700, opacity: 0.9 }}>
          Refer a friend
        </div>
        <span data-testid="referral-offer-pill" style={{
          background: discount > 0 ? "#FFFBC9" : "#0F3A54",
          color: discount > 0 ? "#68280E" : "#FFFBC9",
          borderRadius: 999, padding: "4px 12px", fontSize: 11,
          letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700,
        }}>
          {discount > 0 ? `${discount}% off unlocked` : "10% off total billing"}
        </span>
      </div>

      <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: "-0.028em", fontSize: 28, lineHeight: 1.2, textTransform: "lowercase" }}>
        {discount > 0
          ? `you've unlocked ${discount}% off your total folk billing.`
          : "know someone building their home? send folk their way."}
      </div>

      <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 15, lineHeight: 1.65, opacity: 0.95 }}>
        Share your personal invite. When a friend signs up through your link and pays their advance, we automatically knock <strong>10% off your total Folk billing</strong>, no strings, no expiry.
      </div>

      {shareUrl && (
        <div
          data-testid="referral-share-url"
          style={{
            background: discount > 0 ? "rgba(255, 251, 201, 0.14)" : "#FFF",
            border: `1px solid ${discount > 0 ? "rgba(255, 251, 201, 0.24)" : "rgba(15, 58, 84, 0.18)"}`,
            borderRadius: 12,
            padding: "10px 14px",
            fontFamily: "Host Grotesk, sans-serif",
            fontSize: 13.5,
            color: discount > 0 ? "#FFFBC9" : "#1B1A1A",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {shareUrl}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <a
          data-testid="referral-wa-btn"
          href={waHref}
          target="_blank"
          rel="noreferrer"
          className="folk-pill"
          style={{ background: "#25D366", color: "#FFF", border: "1.5px solid #1DA851", textDecoration: "none", padding: "12px 22px", fontSize: 14, fontWeight: 600 }}
        >
          Share on WhatsApp
        </a>
        <button
          type="button"
          data-testid="referral-copy-btn"
          onClick={copy}
          className="folk-pill"
          style={{
            background: discount > 0 ? "transparent" : "#FFF",
            color: discount > 0 ? "#FFFBC9" : "#0F3A54",
            border: `1.5px solid ${discount > 0 ? "#FFFBC9" : "#0F3A54"}`,
            padding: "12px 22px",
            fontSize: 14,
          }}
        >
          {copied ? "Copied!" : "Copy invite link"}
        </button>
      </div>

      {state && state.referrals_total > 0 && (
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 4, fontFamily: "Host Grotesk, sans-serif", fontSize: 12.5, opacity: 0.85 }}>
          <div>{state.referrals_total} invited</div>
          <div>{state.referrals_converted} signed up</div>
          <div>{discount}% discount so far</div>
        </div>
      )}
    </div>
  );
}

const smallPill = {
  padding: "7px 14px",
  borderRadius: 999,
  border: "1.5px solid #0F3A54",
  background: "transparent",
  color: "#0F3A54",
  fontFamily: "Host Grotesk, sans-serif",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
};
